// src/resources/dashboards/ProductEvolution/sections/BillingSection.tsx
//
// O lado comercial da rede: MRR, faturas do período, inadimplência, receita que
// deixou de ser cobrada e quanto tempo o posto leva do contrato à primeira
// fatura.
//
// Duas honestidades obrigatórias aqui:
//   · a receita não capturada mostra SEPARADAMENTE quantos postos foram
//     valorados pela mediana da carteira — esses são estimativa, não dívida;
//   · `revenuePerFillin` só compara postos que têm fatura NA MESMA competência
//     do volume, e a `note` do backend diz isso na tela.
import React from 'react';
import {
  Box, Table, TableBody, TableCell, TableHead, TableRow, Typography,
} from '@mui/material';
import PaymentsIcon from '@mui/icons-material/Payments';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { COLORS, EmptyHint, ErrorCard, SectionHeader, SkeletonCard, cardSx, fmtBrl, fmtNum } from '../../shared';
import type { SectionState } from '../../shared';
import { ChainBillingData, ImplantationStats } from '../types';
import {
  BackendNote, Card, ShowingCount, Stat, StatGrid, fmtBrlPrecise, fmtDate, fmtPctSmall,
} from './kit';

const HEIGHT = 620;
const PLACES_MAX_HEIGHT = 300;

/** Rótulo pt-BR do status da fatura (`client_bill.status`). */
const BILL_STATUS_LABEL: Record<string, string> = {
  pending: 'Pendente',
  paid: 'Paga',
  canceled: 'Cancelada',
};

const BILL_STATUS_COLOR: Record<string, string> = {
  pending: COLORS.warning,
  paid: COLORS.success,
  canceled: COLORS.faint,
};

const Th = ({ children, align = 'right' }: { children: React.ReactNode; align?: 'left' | 'right' }) => (
  <TableCell align={align} sx={{
    borderBottom: '1px solid rgba(120,105,223,0.12)', whiteSpace: 'nowrap', py: 0.75,
    fontSize: 10.5, fontWeight: 800, letterSpacing: 0.4, textTransform: 'uppercase', color: COLORS.muted,
    position: 'sticky', top: 0, bgcolor: '#fff', zIndex: 1,
  }}>
    {children}
  </TableCell>
);

const Td = ({ children, align = 'right', bold, color }: {
  children: React.ReactNode; align?: 'left' | 'right'; bold?: boolean; color?: string;
}) => (
  <TableCell align={align} sx={{
    borderBottom: '1px solid rgba(148,163,184,0.16)', py: 0.75, whiteSpace: 'nowrap',
    fontSize: 12.5, fontWeight: bold ? 700 : 400, color: color ?? COLORS.ink,
  }}>
    {children}
  </TableCell>
);

const days = (v: number | null) => (v === null ? '—' : `${fmtNum(v)} d`);

const StatsLine = ({ label, stats }: { label: string; stats: ImplantationStats }) => (
  <Typography sx={{ fontSize: 12.5, color: COLORS.ink, lineHeight: 1.75 }}>
    <b>{label}</b>: média <b>{days(stats.avg)}</b> · p90 <b>{days(stats.p90)}</b> ·
    {' '}mín {days(stats.min)} · máx {days(stats.max)}
    {' '}<Typography component="span" sx={{ fontSize: 11.5, color: COLORS.faint }}>
      ({fmtNum(stats.count)} posto{stats.count === 1 ? '' : 's'} com data)
    </Typography>
  </Typography>
);

const BillingSection = ({ state }: { state: SectionState<ChainBillingData> }) => {
  if (state.loading && !state.data) return <SkeletonCard height={HEIGHT} />;
  if (state.error) return <ErrorCard onRetry={state.refetch} height={HEIGHT} />;
  if (!state.data) return null;

  const { mrr, places, period, overdue, lastBill, revenuePerFillin, uncapturedRevenue, implantation } = state.data;
  const totalPlaces = implantation.places.length;

  return (
    <Box>
      <SectionHeader
        icon={<PaymentsIcon sx={{ fontSize: 18 }} />}
        gradient={`linear-gradient(135deg, ${COLORS.success}, ${COLORS.teal})`}
        title="Comercial e faturamento"
        subtitle="MRR, faturas do período, inadimplência, receita não capturada e tempo de implantação"
      />

      <Box sx={{ display: 'grid', gap: 1.75 }}>
        <Box sx={cardSx}>
          <StatGrid min={158}>
            <Stat label="MRR" value={fmtBrl(mrr)} color={COLORS.success}
              sub={`${fmtNum(places.active)} postos ativos · ${fmtNum(places.withBillingStart)} com faturamento`} />
            <Stat label="Faturas no período" value={fmtNum(period.bills)} sub={fmtBrl(period.value)} />
            <Stat label="Situação das faturas"
              value={`${fmtNum(period.paid)} / ${fmtNum(period.pending)} / ${fmtNum(period.canceled)}`}
              sub="pagas / pendentes / canceladas" />
            <Stat label="Comissão de revenda" value={fmtBrl(period.retailerValue)} color={COLORS.purple}
              sub={period.retailerShare === null
                ? 'sem fatura no período'
                : `${fmtPctSmall(period.retailerShare)} do faturado`} />
            <Stat label="Receita por abastecimento"
              value={revenuePerFillin.value === null ? '—' : fmtBrlPrecise(revenuePerFillin.value, 3)}
              color={COLORS.brand}
              sub={`${fmtBrl(revenuePerFillin.billed)} ÷ ${fmtNum(revenuePerFillin.fillins)} ab. · ${fmtNum(revenuePerFillin.matchedPlaces)} postos casados`}
              hint={revenuePerFillin.note} />
          </StatGrid>
          <BackendNote>{revenuePerFillin.note}</BackendNote>
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, gap: 1.75 }}>
          <Card title="Inadimplência" hint="Faturas pendentes com vencimento já passado">
            {overdue.bills === 0 ? (
              <Box sx={{ py: 2 }}>
                <Typography sx={{ fontSize: 15, fontWeight: 800, color: COLORS.success }}>Sem faturas vencidas</Typography>
                <Typography sx={{ fontSize: 12, color: COLORS.muted }}>Nenhuma pendência em aberto para esta rede.</Typography>
              </Box>
            ) : (
              <Box sx={{
                px: 1.75, py: 1.5, borderRadius: 3,
                bgcolor: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.28)',
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <WarningAmberIcon sx={{ fontSize: 20, color: COLORS.danger }} />
                  <Typography sx={{ fontSize: 24, fontWeight: 800, color: COLORS.danger }}>{fmtBrl(overdue.value)}</Typography>
                </Box>
                <Typography sx={{ fontSize: 12.5, color: COLORS.ink, mt: 0.5 }}>
                  <b>{fmtNum(overdue.bills)}</b> fatura{overdue.bills === 1 ? '' : 's'} vencida{overdue.bills === 1 ? '' : 's'}
                  {overdue.oldestDueDate ? <> · a mais antiga venceu em <b>{fmtDate(overdue.oldestDueDate)}</b></> : null}
                </Typography>
              </Box>
            )}

            {lastBill ? (
              <Box sx={{ mt: 1.75, pt: 1.5, borderTop: '1px solid rgba(148,163,184,0.2)' }}>
                <Typography sx={{ fontSize: 11, fontWeight: 800, letterSpacing: 0.5, textTransform: 'uppercase', color: COLORS.faint, mb: 0.5 }}>
                  Última fatura emitida
                </Typography>
                <Typography sx={{ fontSize: 12.5, color: COLORS.ink, lineHeight: 1.7 }}>
                  {fmtBrl(lastBill.value)} ·{' '}
                  <Box component="span" sx={{ fontWeight: 800, color: BILL_STATUS_COLOR[lastBill.status] ?? COLORS.muted }}>
                    {BILL_STATUS_LABEL[lastBill.status] ?? lastBill.status}
                  </Box>
                  {lastBill.placeName ? ` · ${lastBill.placeName}` : ''}
                </Typography>
                <Typography sx={{ fontSize: 11.5, color: COLORS.muted }}>
                  competência {lastBill.monthDate ?? '—'}
                  {' '}· vence {fmtDate(lastBill.dueDate)}
                  {lastBill.paymentDate ? ` · paga em ${fmtDate(lastBill.paymentDate)}` : ''}
                  {' '}· emitida em {fmtDate(lastBill.createdAt)}
                </Typography>
              </Box>
            ) : (
              <Typography sx={{ fontSize: 12, color: COLORS.faint, mt: 1.75 }}>Nenhuma fatura registrada para esta rede.</Typography>
            )}
          </Card>

          <Card title="Receita não capturada" hint="Postos com volume relevante, fora da carência e sem fatura">
            {uncapturedRevenue.places === 0 ? (
              <Box sx={{ py: 2 }}>
                <Typography sx={{ fontSize: 15, fontWeight: 800, color: COLORS.success }}>Nenhum posto rodando sem fatura</Typography>
                <Typography sx={{ fontSize: 12, color: COLORS.muted }}>
                  Todo posto com pelo menos {fmtNum(uncapturedRevenue.minFillins)} abastecimentos no período tem fatura.
                </Typography>
              </Box>
            ) : (
              <>
                <Typography sx={{ fontSize: 26, fontWeight: 800, color: COLORS.warning }}>
                  {fmtBrl(uncapturedRevenue.value)}
                </Typography>
                <Typography sx={{ fontSize: 12.5, color: COLORS.ink, lineHeight: 1.7 }}>
                  <b>{fmtNum(uncapturedRevenue.places)}</b> posto{uncapturedRevenue.places === 1 ? '' : 's'} com pelo menos
                  {' '}{fmtNum(uncapturedRevenue.minFillins)} abastecimentos no período, carência vencida e sem fatura.
                </Typography>
                {/* Estimativa separada: postos sem preço próprio entram pela
                    mediana da carteira e NÃO podem virar "receita certa". */}
                <Box sx={{
                  mt: 1.25, px: 1.5, py: 1, borderRadius: 2.5,
                  bgcolor: uncapturedRevenue.estimatedPlaces ? 'rgba(245,158,11,0.10)' : 'rgba(148,163,184,0.10)',
                  border: `1px solid ${uncapturedRevenue.estimatedPlaces ? 'rgba(245,158,11,0.30)' : 'rgba(148,163,184,0.25)'}`,
                }}>
                  <Typography sx={{ fontSize: 12, color: COLORS.ink, lineHeight: 1.6 }}>
                    {uncapturedRevenue.estimatedPlaces === 0 ? (
                      <>Todos os postos foram valorados pelo <b>preço mensal do próprio posto</b>.</>
                    ) : (
                      <>
                        <b>{fmtNum(uncapturedRevenue.estimatedPlaces)}</b> destes postos não têm preço mensal cadastrado e
                        {' '}entraram <b>estimados</b> pela mediana da carteira
                        {uncapturedRevenue.portfolioMedianPrice !== null
                          ? <> ({fmtBrl(uncapturedRevenue.portfolioMedianPrice)}/mês)</>
                          : null}. Essa parte é estimativa, não receita certa.
                      </>
                    )}
                  </Typography>
                </Box>
              </>
            )}
          </Card>
        </Box>

        <Card
          title="Tempo de implantação"
          hint="Do início da instalação até o começo do faturamento e até a primeira fatura emitida"
          right={<ShowingCount shown={totalPlaces} total={totalPlaces} noun="posto" />}
        >
          <Box sx={{ mb: 1.5 }}>
            <StatsLine label="Até o início do faturamento" stats={implantation.toBillingStart} />
            <StatsLine label="Até a primeira fatura" stats={implantation.toFirstBill} />
          </Box>

          {!totalPlaces ? (
            <EmptyHint text="Nenhum posto cadastrado nesta rede." />
          ) : (
            <Box sx={{ maxHeight: PLACES_MAX_HEIGHT, overflow: 'auto' }}>
              <Table size="small" stickyHeader sx={{ minWidth: 780 }}>
                <TableHead>
                  <TableRow>
                    <Th align="left">Posto</Th>
                    <Th>Preço mensal</Th>
                    <Th>Carência</Th>
                    <Th>Instalação</Th>
                    <Th>Início faturamento</Th>
                    <Th>1ª fatura</Th>
                    <Th>Até faturar</Th>
                    <Th>Até 1ª fatura</Th>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {implantation.places.map(p => (
                    <TableRow key={p.placeId} hover sx={{ bgcolor: p.uncaptured ? 'rgba(245,158,11,0.07)' : undefined }}>
                      <Td align="left" bold>
                        {p.name}
                        {!p.isActive && <Typography component="span" sx={{ fontSize: 10.5, color: COLORS.faint, ml: 0.75 }}>inativo</Typography>}
                        {p.uncaptured && (
                          <Typography component="span" sx={{ fontSize: 10.5, color: COLORS.warning, fontWeight: 800, ml: 0.75 }}>
                            sem fatura
                          </Typography>
                        )}
                      </Td>
                      <Td>{p.monthlyPrice === null ? '—' : fmtBrl(p.monthlyPrice)}</Td>
                      <Td>{p.gracePeriod === null ? '—' : `${fmtNum(p.gracePeriod)} m`}</Td>
                      <Td>{fmtDate(p.installationStartDate ?? p.createdAt)}</Td>
                      <Td>{fmtDate(p.billingStartDate)}</Td>
                      <Td>{fmtDate(p.firstBillAt)}</Td>
                      <Td bold>{days(p.daysToBillingStart)}</Td>
                      <Td bold>{days(p.daysToFirstBill)}</Td>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}
          <BackendNote>
            Quando o posto não tem data de instalação, a contagem parte da data de cadastro.
            {' '}Linhas em âmbar são os postos que entraram na receita não capturada.
          </BackendNote>
        </Card>
      </Box>
    </Box>
  );
};

export default BillingSection;
